import { Option } from '@beeline/design-system-react/extracted-styles';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import { ProgressState } from 'src/types';

import { startAppListening } from '../listenerMiddleware';

import { emptyBookings, initialState } from './initialState';
import { fetchBookings, unbookBooking } from './services';
import { BookingsPageMode, BookingsPageRole } from './types';

export const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    setFrom: (state, action: PayloadAction<string | undefined>) => {
      state.from = action.payload;
    },
    setTo: (state, action: PayloadAction<string | undefined>) => {
      state.to = action.payload;
    },
    setBookingObjectType: (
      state,
      action: PayloadAction<BookingObjectType | undefined>,
    ) => {
      state.bookingObjectType = action.payload;
    },
    applyFilters: (
      state,
      action: PayloadAction<{
        from: string | undefined;
        to: string | undefined;
      }>,
    ) => {
      state.from = action.payload.from;
      state.to = action.payload.to;
    },
    resetFilters: state => {
      state.from = undefined;
      state.to = undefined;
    },
    setMode: (state, action: PayloadAction<BookingsPageMode | undefined>) => {
      state.mode = action.payload;
    },
    setRole: (state, action: PayloadAction<BookingsPageRole | undefined>) => {
      state.role = action.payload;
    },
    setUnbookingState: (
      state,
      action: PayloadAction<{ id: string; value: ProgressState }>,
    ) => {
      state.unbookings[action.payload.id] = action.payload.value;
    },
    setEmployee: (state, action: PayloadAction<Option<string> | null>) => {
      state.employee = action.payload;
    },
    setFiltersAreOpened: (state, action: PayloadAction<boolean>) => {
      state.filtersAreOpened = action.payload;
    },
    openFilters: state => {
      state.filtersAreOpened = true;
    },
    closeFilters: state => {
      state.filtersAreOpened = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchBookings.pending, (state, action) => {
      if (action.meta.arg) {
        state.rows = emptyBookings;
        state.hasMore = true;
      }
      state.error = undefined;
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchBookings.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchBookings.fulfilled, (state, action) => {
      if (!action.payload || state.currentRequestId !== action.meta.requestId)
        return;

      state.loading = false;
      state.hasMore = !!action.payload.nextPageToken;
      state.token = action.payload.nextPageToken;
      state.rows = action.meta.arg
        ? action.payload.bookings
        : [...state.rows, ...action.payload.bookings];
      state.now = action.payload.now;
    });

    builder.addCase(unbookBooking.pending, (state, action) => {
      state.unbookings[action.meta.arg.bookingId] = { state: 'loading' };
    });
    builder.addCase(unbookBooking.rejected, (state, action) => {
      state.unbookings[action.meta.arg.bookingId] = { state: 'error' };
    });
    builder.addCase(unbookBooking.fulfilled, (state, action) => {
      state.unbookings[action.meta.arg.bookingId] = { state: 'success' };
    });
  },
});

export const { actions: bookingsActions, reducer: bookingsReducer } =
  bookingsSlice;

startAppListening({
  predicate: (action, current, previous) => {
    return (
      current.bookings.bookingObjectType !==
        previous.bookings.bookingObjectType ||
      current.bookings.from !== previous.bookings.from ||
      current.bookings.to !== previous.bookings.to ||
      current.bookings.mode !== previous.bookings.mode ||
      current.bookings.role !== previous.bookings.role ||
      current.bookings.employee?.id !== previous.bookings.employee?.id
    );
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchBookings(true));
  },
});
